import { useEffect, useRef, useState } from "react";
import {
  listSiteImages,
  uploadSiteImage,
  updateSiteImage,
  deleteSiteImage,
  SiteImage,
} from "../../../lib/adminApi";
import { useToast } from "../../../components/Toast";

const CATEGORIES: { value: string; label: string; description: string }[] = [
  {
    value: "hero",
    label: "Hero",
    description: "Velká úvodní fotka na homepage (zobrazí se jako pozadí s blur overlay)",
  },
  {
    value: "gallery",
    label: "Galerie",
    description: "Fotky z akcí pro sekci galerie na homepage",
  },
  {
    value: "about",
    label: "O nás",
    description: "Fotky pro případnou stránku O nás",
  },
];

export default function SiteImagesAdmin() {
  const toast = useToast();
  const [category, setCategory] = useState("hero");
  const [images, setImages] = useState<SiteImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function reload() {
    setLoading(true);
    listSiteImages(category)
      .then(setImages)
      .catch((e) => toast.show("error", e.message))
      .finally(() => setLoading(false));
  }
  useEffect(reload, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadSiteImage(file, { category, sortOrder: images.length * 10 });
      toast.show("success", "Obrázek nahrán.");
      reload();
    } catch (err: any) {
      toast.show("error", err.message ?? "Upload selhal.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function onDelete(img: SiteImage) {
    if (!confirm("Smazat obrázek?")) return;
    try {
      await deleteSiteImage(img.id);
      toast.show("success", "Smazáno.");
      reload();
    } catch (err: any) {
      toast.show("error", err.message);
    }
  }

  async function onUpdate(img: SiteImage, patch: Partial<SiteImage>) {
    try {
      await updateSiteImage(img.id, patch as any);
      reload();
    } catch (err: any) {
      toast.show("error", err.message);
    }
  }

  const cat = CATEGORIES.find((c) => c.value === category)!;

  return (
    <div>
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Fotky na webu</h1>
        <p className="mt-1 text-slate-600">
          Nahrajte fotky z akcí, aby byl web plný života. Hero = jedna velká úvodní fotka, Galerie
          = mřížka fotek na domovské stránce.
        </p>
      </div>

      {/* Taby kategorií */}
      <div className="mt-6 flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {CATEGORIES.map((c) => (
          <button
            key={c.value}
            onClick={() => setCategory(c.value)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
              category === c.value
                ? "bg-obloha-indigo-700 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="mt-4 text-sm text-slate-500">{cat.description}</div>

      {/* Upload tlačítko */}
      <div className="mt-6">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFileChange} />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="btn-primary"
        >
          {uploading ? "Nahrávám…" : "📷 Nahrát obrázek"}
        </button>
      </div>

      {/* Grid */}
      <div className="mt-8">
        {loading ? (
          <div className="text-slate-500">Načítám…</div>
        ) : images.length === 0 ? (
          <div className="card text-center text-slate-500">
            <div className="py-8">
              <div className="text-5xl">📸</div>
              <div className="mt-4">Zatím žádné {cat.label.toLowerCase()} fotky.</div>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {images.map((img) => (
              <div key={img.id} className="card overflow-hidden p-0">
                <div className="aspect-video w-full bg-slate-100">
                  <img src={img.url} alt={img.alt ?? ""} className="h-full w-full object-cover" />
                </div>
                <div className="p-4">
                  <input
                    className="input text-sm"
                    placeholder="Alt text (pro čtečku obrazovek)"
                    defaultValue={img.alt ?? ""}
                    onBlur={(e) =>
                      e.target.value !== (img.alt ?? "") &&
                      onUpdate(img, { alt: e.target.value || null })
                    }
                  />
                  <input
                    className="input mt-2 text-sm"
                    placeholder="Popisek (hover overlay)"
                    defaultValue={img.caption ?? ""}
                    onBlur={(e) =>
                      e.target.value !== (img.caption ?? "") &&
                      onUpdate(img, { caption: e.target.value || null })
                    }
                  />
                  <div className="mt-3 flex items-center justify-between">
                    <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        checked={img.active}
                        onChange={(e) => onUpdate(img, { active: e.target.checked })}
                        className="h-4 w-4"
                      />
                      Zobrazit na webu
                    </label>
                    <button
                      onClick={() => onDelete(img)}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Smazat
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
